// src/pages/ProfileSettings.jsx
import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import Navbar from '../components/Navbar';
import { useUser } from '../hooks/useUser';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';

// ─── Layout ──────────────────────────────────────────────────────────────────

const ProfileContainer = styled.div`
  min-height: 100vh;
  background-color: #f9fafb;
`;

const ContentContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem;
`;

const ProfileCard = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
  overflow: hidden;
  margin-bottom: 1.5rem;
`;

// ─── Header ──────────────────────────────────────────────────────────────────

const ProfileHeader = styled.div`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 2rem;
  text-align: center;
  color: white;
`;

const AvatarContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 1rem;
`;

const Avatar = styled.img`
  width: 120px;
  height: 120px;
  border-radius: 50%;
  border: 4px solid rgba(255, 255, 255, 0.2);
  object-fit: cover;
`;

const UserName = styled.h1`
  font-size: 1.875rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
`;

const UserRole = styled.p`
  font-size: 1rem;
  opacity: 0.9;
`;

// ─── Body ─────────────────────────────────────────────────────────────────────

const ProfileBody = styled.div`
  padding: 2rem;
`;

const SectionTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 1.5rem;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid #e5e7eb;
`;

const InfoGrid = styled.div`
  display: grid;
  gap: 1.5rem;
`;

const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const InfoLabel = styled.label`
  font-size: 0.875rem;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const InfoValue = styled.div`
  font-size: 1rem;
  color: #374151;
  padding: 0.75rem 1rem;
  background-color: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-family: 'Courier New', monospace;
`;

// ─── API Keys ─────────────────────────────────────────────────────────────────

const ApiKeySection = styled.div`
  margin-top: 2rem;
  padding-top: 2rem;
  border-top: 1px solid #e5e7eb;
`;

const GenerateRow = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
`;

const KeyLabelInput = styled.input`
  flex: 1;
  padding: 0.625rem 0.875rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.9rem;
  color: #374151;
  outline: none;

  &:focus {
    border-color: #6366f1;
    box-shadow: 0 0 0 2px rgba(99, 102, 241, 0.15);
  }
`;

const Button = styled.button`
  padding: 0.625rem 1.25rem;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  border: none;
  transition: background 0.15s;

  ${({ variant }) => variant === 'primary' ? `
    background: #6366f1;
    color: white;
    &:hover { background: #4f46e5; }
    &:disabled { background: #a5b4fc; cursor: not-allowed; }
  ` : `
    background: #fef2f2;
    color: #dc2626;
    border: 1px solid #fecaca;
    &:hover { background: #fee2e2; }
  `}
`;

const NewKeyBanner = styled.div`
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-radius: 8px;
  padding: 1rem 1.25rem;
  margin-bottom: 1.5rem;
`;

const NewKeyLabel = styled.div`
  font-size: 0.8rem;
  font-weight: 600;
  color: #166534;
  margin-bottom: 0.5rem;
`;

const NewKeyRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const NewKeyValue = styled.code`
  flex: 1;
  font-size: 0.875rem;
  font-family: 'Courier New', monospace;
  color: #15803d;
  background: #dcfce7;
  padding: 0.5rem 0.75rem;
  border-radius: 4px;
  word-break: break-all;
`;

const CopyButton = styled.button`
  padding: 0.4rem 0.75rem;
  border-radius: 4px;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid #86efac;
  background: white;
  color: #166534;
  white-space: nowrap;
  transition: background 0.15s;

  &:hover { background: #f0fdf4; }
`;

const KeyList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const KeyRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.875rem 1rem;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fafafa;
`;

const KeyMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
`;

const KeyName = styled.span`
  font-size: 0.9rem;
  font-weight: 600;
  color: #1f2937;
`;

const KeyDetails = styled.span`
  font-size: 0.775rem;
  color: #6b7280;
  font-family: 'Courier New', monospace;
`;

const EmptyKeys = styled.p`
  color: #9ca3af;
  font-size: 0.9rem;
  text-align: center;
  padding: 1.5rem 0;
`;

// ─── Feedback ─────────────────────────────────────────────────────────────────

const AlertBanner = styled.div`
  padding: 0.75rem 1rem;
  border-radius: 6px;
  margin-bottom: 1rem;
  font-size: 0.875rem;

  ${({ type }) => type === 'error' ? `
    background: #fef2f2;
    border: 1px solid #fecaca;
    color: #dc2626;
  ` : `
    background: #eff6ff;
    border: 1px solid #bfdbfe;
    color: #1d4ed8;
  `}
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
`;

const Spinner = styled.div`
  width: 40px;
  height: 40px;
  border: 4px solid #e5e7eb;
  border-top: 4px solid #3b82f6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
`;

// ─── Component ───────────────────────────────────────────────────────────────

const ProfileSettings = () => {
  const navigate = useNavigate();
  const { user, loading, error, getAvatarUrl } = useUser();

  const [apiKeys, setApiKeys] = useState([]);
  const [keysLoading, setKeysLoading] = useState(false);
  const [newKeyLabel, setNewKeyLabel] = useState('');
  const [generating, setGenerating] = useState(false);
  const [newKey, setNewKey] = useState(null);   // shown once after generation
  const [copied, setCopied] = useState(false);
  const [keyError, setKeyError] = useState(null);

  const fetchKeys = useCallback(async () => {
    setKeysLoading(true);
    try {
      const data = await apiService.listApiKeys();
      setApiKeys(data.api_keys || []);
    } catch (e) {
      setKeyError('Failed to load API keys.');
    } finally {
      setKeysLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user) fetchKeys();
  }, [user, fetchKeys]);

  const handleGenerate = async () => {
    if (!newKeyLabel.trim()) return;
    setGenerating(true);
    setKeyError(null);
    setNewKey(null);
    try {
      const result = await apiService.generateApiKey(newKeyLabel.trim());
      setNewKey(result.api_key);
      setNewKeyLabel('');
      await fetchKeys();
    } catch (e) {
      setKeyError('Failed to generate API key.');
    } finally {
      setGenerating(false);
    }
  };

  const handleRevoke = async (keyId) => {
    setKeyError(null);
    try {
      await apiService.revokeApiKey(keyId);
      setApiKeys(prev => prev.filter(k => k.key_id !== keyId));
      if (newKey) setNewKey(null);
    } catch (e) {
      setKeyError('Failed to revoke API key.');
    }
  };

  const handleCopy = () => {
    if (!newKey) return;
    navigator.clipboard.writeText(newKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const formatDate = (d) => {
    if (!d) return 'Never';
    try { return new Date(d).toLocaleString('en-US', { timeZone: 'UTC', timeZoneName: 'short' }); }
    catch { return 'N/A'; }
  };

  if (loading) {
    return (
      <ProfileContainer>
        <Navbar onNavigate={(p) => navigate(p)} />
        <ContentContainer><LoadingContainer><Spinner /></LoadingContainer></ContentContainer>
      </ProfileContainer>
    );
  }

  if (error || !user) {
    return (
      <ProfileContainer>
        <Navbar onNavigate={(p) => navigate(p)} />
        <ContentContainer>
          <AlertBanner type="error">{error || 'No user data available'}</AlertBanner>
        </ContentContainer>
      </ProfileContainer>
    );
  }

  const avatarUrl = getAvatarUrl
    ? getAvatarUrl()
    : `https://robohash.org/${user.email}?set=set1&size=200x200`;

  return (
    <ProfileContainer>
      <Navbar onNavigate={(p) => navigate(p)} />
      <ContentContainer>

        {/* Account info */}
        <ProfileCard>
          <ProfileHeader>
            <AvatarContainer>
              <Avatar
                src={avatarUrl}
                alt={`${user.email} avatar`}
                onError={(e) => { e.target.src = 'https://robohash.org/default?set=set1&size=200x200'; }}
              />
            </AvatarContainer>
            <UserName>{user.email}</UserName>
            <UserRole>Security Analyst</UserRole>
          </ProfileHeader>

          <ProfileBody>
            <SectionTitle>Account Information</SectionTitle>
            <InfoGrid>
              <InfoItem>
                <InfoLabel>Email Address</InfoLabel>
                <InfoValue>{user.email}</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>User ID</InfoLabel>
                <InfoValue>{user.id}</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Last Login</InfoLabel>
                <InfoValue>{formatDate(user.last_login)}</InfoValue>
              </InfoItem>
            </InfoGrid>
          </ProfileBody>
        </ProfileCard>

        {/* API Keys */}
        <ProfileCard>
          <ProfileBody>
            <SectionTitle>API Keys</SectionTitle>

            {keyError && <AlertBanner type="error">{keyError}</AlertBanner>}

            <GenerateRow>
              <KeyLabelInput
                type="text"
                placeholder="Key label (e.g. My Script)"
                value={newKeyLabel}
                onChange={(e) => setNewKeyLabel(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleGenerate()}
                maxLength={64}
              />
              <Button
                variant="primary"
                onClick={handleGenerate}
                disabled={generating || !newKeyLabel.trim()}
              >
                {generating ? 'Generating…' : 'Generate Key'}
              </Button>
            </GenerateRow>

            {newKey && (
              <NewKeyBanner>
                <NewKeyLabel>New API key — copy it now, it will not be shown again</NewKeyLabel>
                <NewKeyRow>
                  <NewKeyValue>{newKey}</NewKeyValue>
                  <CopyButton onClick={handleCopy}>
                    {copied ? 'Copied!' : 'Copy'}
                  </CopyButton>
                </NewKeyRow>
              </NewKeyBanner>
            )}

            {keysLoading ? (
              <EmptyKeys>Loading keys…</EmptyKeys>
            ) : apiKeys.length === 0 ? (
              <EmptyKeys>No API keys yet. Generate one above.</EmptyKeys>
            ) : (
              <KeyList>
                {apiKeys.map((k) => (
                  <KeyRow key={k.key_id}>
                    <KeyMeta>
                      <KeyName>{k.label}</KeyName>
                      <KeyDetails>
                        {k.prefix}••••••••  ·  Created {formatDate(k.created_at)}
                        {k.last_used_at && `  ·  Last used ${formatDate(k.last_used_at)}`}
                      </KeyDetails>
                    </KeyMeta>
                    <Button variant="danger" onClick={() => handleRevoke(k.key_id)}>
                      Revoke
                    </Button>
                  </KeyRow>
                ))}
              </KeyList>
            )}
          </ProfileBody>
        </ProfileCard>

      </ContentContainer>
    </ProfileContainer>
  );
};

export default ProfileSettings;
